import React, { useState, useEffect } from "react";
import apiFetch from "../../utils/apiFetch";
import SmModal from "./SmModal";

const ACTION_LABELS = {
  create: "яратди",
  update: "таҳрирлади",
  delete: "ўчирди",
  restore: "тиклади",
};

const ENTITY_LABELS = {
  substations: "Подстанция",
  substation_transformers: "Трансформатор",
  substation_breakers: "Ўчиргич",
  substation_disconnectors: "Ажратгич",
  substation_reactors: "Реактор",
  substation_arresters: "Разрядник",
  substation_current_transformers: "Ток трансформатори",
  substation_voltage_transformers: "Кучланиш трансформатори",
  substation_batteries: "Аккумулятор",
  substation_schemes: "Схема",
  network: "Тармоқ",
  poles: "Опора",
  network_inspection: "Эксплуатация ва таъмир (тармоқ)",
  sub_transformer_maintenance: "Эксплуатация ва таъмир (трансф.)",
  sub_breaker_maintenance: "Эксплуатация ва таъмир (ўчиргич)",
  sub_disconnector_maintenance: "Эксплуатация ва таъмир (ажратгич)",
  sub_reactor_maintenance: "Эксплуатация ва таъмир (реактор)",
  sub_arrester_maintenance: "Эксплуатация ва таъмир (разрядник)",
  sub_current_transformer_maintenance: "Эксплуатация ва таъмир (ток тр.)",
  sub_voltage_transformer_maintenance: "Эксплуатация ва таъмир (кучл. тр.)",
  sub_battery_maintenance: "Эксплуатация ва таъмир (аккум.)",
};

// Excluded keys from diff
const SKIP_KEYS = new Set(["id", "deleted_at", "deleted_by", "date_created", "slug", "name_normalized"]);

export default function AuditLogModal({ substationId, apiUrl, onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const res = await apiFetch(apiUrl || `/api/substation-management/${substationId}/audit-log`);
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) setLogs(data);
        }
      } catch (e) { console.error(e); }
      setLoading(false);
    }
    load();
  }, [substationId]);

  function fmtTime(d) {
    if (!d) return "";
    return new Date(d).toLocaleString("uz-UZ", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  }

  function renderValue(val) {
    if (val === null || val === undefined) return "—";
    if (typeof val === "object") return JSON.stringify(val, null, 2);
    return String(val);
  }

  function renderSnapshot(data) {
    if (!data) return null;

    // Full substation snapshot (has .substation and .equipment)
    if (data.substation) {
      return (
        <div style={{ fontSize: 13, marginTop: 6 }}>
          <strong>Подстанция:</strong>
          <div className="sm-audit__diff" style={{ marginTop: 4 }}>
            {Object.entries(data.substation).filter(([k]) => !SKIP_KEYS.has(k) && data.substation[k] != null).map(([k, v]) => (
              <div key={k}><strong>{k}:</strong> {renderValue(v)}</div>
            ))}
          </div>
          {data.equipment && Object.entries(data.equipment).map(([type, eq]) => (
            <div key={type} style={{ marginTop: 8 }}>
              <strong>{ENTITY_LABELS[`substation_${type}`] || type} ({eq.items?.length || 0}):</strong>
              {eq.items?.map((item, i) => {
                const itemMaint = (eq.maintenance || []).filter(m => Object.values(m).includes(item.id));
                return (
                  <div key={i} style={{ marginTop: 4 }}>
                    <div className="sm-audit__diff">
                      {Object.entries(item).filter(([k]) => !SKIP_KEYS.has(k) && item[k] != null).map(([k, v]) => (
                        <div key={k}><strong>{k}:</strong> {renderValue(v)}</div>
                      ))}
                    </div>
                    {itemMaint.length > 0 && (
                      <div style={{ marginTop: 4, paddingLeft: 16 }}>
                        <em style={{ color: "#64748b" }}>Эксплуатация ва таъмир ({itemMaint.length}):</em>
                        {itemMaint.map((m, j) => (
                          <div key={j} className="sm-audit__diff" style={{ marginTop: 2 }}>
                            {Object.entries(m).filter(([k]) => !SKIP_KEYS.has(k) && m[k] != null).map(([k, v]) => (
                              <div key={k}><strong>{k}:</strong> {renderValue(v)}</div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
          {data.schemes?.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <strong>Схемалар ({data.schemes.length}):</strong>
              {data.schemes.map((s, i) => (
                <div key={i} className="sm-audit__diff" style={{ marginTop: 4 }}>
                  {Object.entries(s).filter(([k]) => !SKIP_KEYS.has(k) && s[k] != null).map(([k, v]) => (
                    <div key={k}><strong>{k}:</strong> {renderValue(v)}</div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Network snapshot (has .network)
    if (data.network) {
      return (
        <div style={{ fontSize: 13, marginTop: 6 }}>
          <strong>Тармоқ:</strong>
          <div className="sm-audit__diff" style={{ marginTop: 4 }}>
            {Object.entries(data.network).filter(([k]) => !SKIP_KEYS.has(k) && data.network[k] != null).map(([k, v]) => (
              <div key={k}><strong>{k}:</strong> {renderValue(v)}</div>
            ))}
          </div>
          {data.poles?.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <strong>Опоралар ({data.poles.length}):</strong>
              {data.poles.map((p, i) => (
                <div key={i} className="sm-audit__diff" style={{ marginTop: 4 }}>
                  {Object.entries(p).filter(([k]) => !SKIP_KEYS.has(k) && p[k] != null).map(([k, v]) => (
                    <div key={k}><strong>{k}:</strong> {renderValue(v)}</div>
                  ))}
                </div>
              ))}
            </div>
          )}
          {data.inspections?.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <strong>Эксплуатация ва таъмир ({data.inspections.length}):</strong>
              {data.inspections.map((r, i) => (
                <div key={i} className="sm-audit__diff" style={{ marginTop: 4 }}>
                  {Object.entries(r).filter(([k]) => !SKIP_KEYS.has(k) && r[k] != null).map(([k, v]) => (
                    <div key={k}><strong>{k}:</strong> {renderValue(v)}</div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Equipment snapshot (has .equipment and .maintenance)
    if (data.equipment) {
      const itemMaint = data.maintenance || [];
      return (
        <div style={{ fontSize: 13, marginTop: 6 }}>
          <div className="sm-audit__diff">
            {Object.entries(data.equipment).filter(([k]) => !SKIP_KEYS.has(k) && data.equipment[k] != null).map(([k, v]) => (
              <div key={k}><strong>{k}:</strong> {renderValue(v)}</div>
            ))}
          </div>
          {itemMaint.length > 0 && (
            <div style={{ marginTop: 6, paddingLeft: 16 }}>
              <em style={{ color: "#64748b" }}>Эксплуатация ва таъмир ({itemMaint.length}):</em>
              {itemMaint.map((m, i) => (
                <div key={i} className="sm-audit__diff" style={{ marginTop: 2 }}>
                  {Object.entries(m).filter(([k]) => !SKIP_KEYS.has(k) && m[k] != null).map(([k, v]) => (
                    <div key={k}><strong>{k}:</strong> {renderValue(v)}</div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Simple flat object (maintenance record, scheme, etc.)
    return (
      <div className="sm-audit__diff" style={{ marginTop: 6 }}>
        {Object.entries(data).filter(([k]) => !SKIP_KEYS.has(k) && data[k] != null).map(([k, v]) => (
          <div key={k}><strong>{k}:</strong> {renderValue(v)}</div>
        ))}
      </div>
    );
  }

  function renderDiff(log) {
    // For deletes — show snapshot nicely
    if (log.action === "delete") {
      return renderSnapshot(log.old_values);
    }

    if (log.action === "create" || log.action === "restore") return null;

    // For updates — show field-by-field diff
    const old = log.old_values || {};
    const nw = log.new_values || {};
    const keys = [...new Set([...Object.keys(old), ...Object.keys(nw)])].filter((k) => !SKIP_KEYS.has(k));
    const changed = keys.filter((k) => JSON.stringify(old[k]) !== JSON.stringify(nw[k]));
    if (!changed.length) return null;
    return (
      <div className="sm-audit__diff">
        {changed.map((k) => (
          <div key={k}>
            <strong>{k}:</strong>{" "}
            <span style={{ color: "#ef4444" }}>{renderValue(old[k])}</span>{" → "}
            <span style={{ color: "#10b981" }}>{renderValue(nw[k])}</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <SmModal title="Ўзгаришлар тарихи" onClose={onClose} wide>
      {loading ? (
        <div className="sm-loading"><div className="sm-spinner" />Юкланмоқда...</div>
      ) : logs.length === 0 ? (
        <p style={{ textAlign: "center", color: "#64748b" }}>Тарих топилмади</p>
      ) : (
        <div>
          {logs.map((l) => (
            <div key={l.id} className="sm-audit__item">
              <div className={`sm-audit__dot sm-audit__dot--${l.action}`} />
              <div className="sm-audit__body">
                <span className="sm-audit__who">
                  {l.user ? `${l.user.name || ""} ${l.user.lastname || ""}`.trim() : "Тизим"}
                </span>
                {" "}
                <span className="sm-audit__action">
                  {ENTITY_LABELS[l.entity_type] || l.entity_type}
                  {l.entity_name ? ` "${l.entity_name}"` : ""}
                  {" "}{ACTION_LABELS[l.action] || l.action}
                </span>
                <div className="sm-audit__time">{fmtTime(l.date_created)}</div>
                {renderDiff(l)}
              </div>
            </div>
          ))}
        </div>
      )}
    </SmModal>
  );
}
